import React, { FC, useCallback, useMemo } from 'react'
import { graphql } from 'react-relay'
import { object, string } from 'yup'
import { Location, useNavigate, useLocation } from 'react-router-dom'
import {
  Form,
  FormField,
  FormSubmitButton,
  FormTextInput,
  Stack,
} from '@components'
import { useMutation } from '@data/useMutation'

import { SignInScreenTokenFormMutation } from './__generated__/SignInScreenTokenFormMutation.graphql'

const VALIDATION_SCHEMA = object().shape({
  email: string().required('Required'),
})

const GENERATE_SIGN_IN_TOKEN_MUTATION = graphql`
  mutation SignInScreenTokenFormMutation(
    $input: GenerateSignInTokenMutationInput!
  ) {
    generateSignInToken(input: $input) {
      __typename
    }
  }
`

export const SignInScreenTokenForm: FC<{
  initialEmail?: string
  setEmail?: (email: string) => any
  authRedirectPath?: string
}> = ({ initialEmail = '', setEmail, authRedirectPath }) => {
  const navigate = useNavigate()
  const location = useLocation()

  const routerState = useMemo(
    () => ({
      state: {
        backgroundLocation: location.state?.backgroundLocation as Location,
        authRedirectPath: location.state?.authRedirectPath as string,
      },
    }),
    [location]
  )

  const onEmailChange = useCallback(
    (value) => {
      if (setEmail) {
        setEmail(value)
      }
    },
    [setEmail]
  )

  const generateSignInTokenMutation =
    useMutation<SignInScreenTokenFormMutation>(GENERATE_SIGN_IN_TOKEN_MUTATION)

  const handleSubmit = useCallback(
    async (values, actions) => {
      const result = await generateSignInTokenMutation({
        variables: {
          input: { email: values.email, authRedirectPath },
        },
      })

      switch (result.generateSignInToken.__typename) {
        case 'GenerateSignInTokenMutationUnknownEmailError':
          navigate(
            `/sign-up?email=${encodeURIComponent(values.email)}`,
            routerState
          )
          break
        case 'GenerateSignInTokenMutationSuccess':
          navigate('/auth/code', routerState)
          break
        default:
          actions.setStatus(
            'We had an error signing you in. Please try again or contact us at hi@guild.host for assistance'
          )
      }
    },
    [generateSignInTokenMutation]
  )

  return (
    <Form
      initialValues={{ email: initialEmail }}
      onSubmit={handleSubmit}
      validationSchema={VALIDATION_SCHEMA}
      validateOnChange={false}
      validateOnBlur={false}
    >
      <Stack gap={6}>
        <FormField name='email' label='E-mail'>
          <FormTextInput
            autoFocus
            placeholder='Enter your e-mail address'
            keyboardType='email-address'
            submitBehaviour='submit-form'
            onChange={onEmailChange}
          />
        </FormField>

        <FormSubmitButton align='flex-end'>Sign In</FormSubmitButton>
      </Stack>
    </Form>
  )
}
